import React from 'react';
import { motion } from 'framer-motion';
import { useParams, useLocation, Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumb from '../components/seo/Breadcrumb';
import NotFoundPage from './NotFoundPage';
import { PROJECTS, PORTFOLIO_STATS, ESTADO_EN } from '../data/projects';
import { isEnglishPath } from '../utils/i18nRoutes';

const TEXT = {
  es: {
    breadcrumbHome: 'Inicio',
    breadcrumbProjects: 'Proyectos',
    aboutTitle: 'Sobre el proyecto',
    statusLabel: 'Estado',
    periodLabel: 'Periodo',
    funderLabel: 'Financiador',
    budgetLabel: 'Presupuesto',
    portfolioLabel: 'Cartera de innovación',
    projectsWord: 'proyectos',
    backToProjects: 'Volver a proyectos',
    ctaContact: 'Contactar equipo I+D',
  },
  en: {
    breadcrumbHome: 'Home',
    breadcrumbProjects: 'Projects',
    aboutTitle: 'About the project',
    statusLabel: 'Status',
    periodLabel: 'Period',
    funderLabel: 'Funder',
    budgetLabel: 'Budget',
    portfolioLabel: 'Innovation portfolio',
    projectsWord: 'projects',
    backToProjects: 'Back to projects',
    ctaContact: 'Contact the R&D team',
  },
};

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const locale = isEnglishPath(pathname) ? 'en' : 'es';
  const project = PROJECTS.find((p) => p.slug === slug);

  if (!project) return <NotFoundPage />;

  const t = TEXT[locale];
  const path = locale === 'en' ? `/en/projects/${project.slug}` : `/proyectos/${project.slug}`;
  const name = project.name;
  const desc = locale === 'en' ? project.descEn : project.desc;
  const longDesc = locale === 'en' ? project.longDescEn : project.longDesc;
  const estado = locale === 'en' ? ESTADO_EN[project.estado] || project.estado : project.estado;
  const periodo = locale === 'en' && project.periodoEn ? project.periodoEn : project.periodo;
  const financiador = locale === 'en' && project.financiadorEn ? project.financiadorEn : project.financiador;
  const projectsPath = locale === 'en' ? '/en/projects' : '/proyectos';
  const contactPath = locale === 'en' ? '/en/contact' : '/contacto';
  const homePath = locale === 'en' ? '/en' : '/';

  const ALTERNATES = [
    { hreflang: 'es', href: `/proyectos/${project.slug}` },
    { hreflang: 'en', href: `/en/projects/${project.slug}` },
    { hreflang: 'x-default', href: `/proyectos/${project.slug}` },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ResearchProject',
    name,
    description: desc,
    url: `https://innovation.valorizasm.com${path}`,
    funder: financiador,
  };

  return (
    <>
      <SEOHead
        title={`${name} | ${locale === 'en' ? 'R&D Projects' : 'Proyectos I+D+i'} | Valoriza`}
        description={desc}
        path={path}
        locale={locale}
        alternates={ALTERNATES}
        jsonLd={[jsonLd]}
      />
      <article className="pt-32 pb-24 v-container" data-testid="project-detail-page">
        <Breadcrumb items={[{ label: t.breadcrumbHome, to: homePath }, { label: t.breadcrumbProjects, to: projectsPath }, { label: name }]} />

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl mb-10">
          <span className="v-tag green mb-6 inline-flex">{estado}</span>
          <h1 className="text-3xl sm:text-4xl font-bold text-black dark:text-white/95 mb-6 tracking-tight" data-testid="project-detail-h1">{name}</h1>
          <p className="text-base sm:text-lg text-gray-700 dark:text-white/65 leading-relaxed">{desc}</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-8">
            <section>
              <h2 className="text-xl font-bold text-black dark:text-white/90 mb-3">{t.aboutTitle}</h2>
              <p className="text-sm sm:text-base text-gray-700 dark:text-white/65 leading-relaxed">{longDesc}</p>
            </section>
          </div>

          <aside className="space-y-6">
            <div className="glass-card p-6 space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-white/40 mb-1">{t.statusLabel}</div>
                <div className="text-sm font-semibold text-black dark:text-white/85">{estado}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-white/40 mb-1">{t.periodLabel}</div>
                <div className="text-sm font-semibold text-black dark:text-white/85">{periodo}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-white/40 mb-1">{t.funderLabel}</div>
                <div className="text-sm font-semibold text-black dark:text-white/85">{financiador}</div>
              </div>
              {project.presupuestoK && (
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-white/40 mb-1">{t.budgetLabel}</div>
                  <div className="text-sm font-semibold text-black dark:text-white/85">{project.presupuestoK} k€</div>
                </div>
              )}
              <div className="pt-2" style={{ borderTop: '1px solid var(--v-border)' }}>
                <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-white/40 mb-1">{t.portfolioLabel}</div>
                <div className="text-sm font-semibold text-black dark:text-white/85">{PORTFOLIO_STATS.presupuesto} · {PORTFOLIO_STATS.total} {t.projectsWord}</div>
              </div>
            </div>
            <Link to={projectsPath} className="btn-secondary w-full flex items-center justify-center gap-2">{t.backToProjects}</Link>
            <Link to={contactPath} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="project-detail-cta-contact">
              {t.ctaContact} <ArrowRight size={15} />
            </Link>
          </aside>
        </div>
      </article>
    </>
  );
}
